import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-md",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-md",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-md",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-md",
        link: "text-primary underline-offset-4 hover:underline",
        apple: "bg-foreground text-background hover:bg-foreground/90 rounded-full",
        "apple-outline": "border-2 border-foreground text-foreground hover:bg-foreground hover:text-background rounded-full",
        "apple-blue": "bg-[hsl(211_100%_50%)] text-[hsl(0_0%_100%)] hover:bg-[hsl(211_100%_45%)] rounded-full glow-button",
        "apple-ghost": "text-[hsl(211_100%_50%)] hover:text-[hsl(211_100%_45%)]",
        whatsapp: "bg-[hsl(142_70%_45%)] text-[hsl(0_0%_100%)] hover:bg-[hsl(142_70%_40%)] rounded-full shadow-[0_2px_8px_-2px_hsl(0_0%_0%/0.08)]",
        call: "bg-[hsl(25_95%_53%)] text-[hsl(0_0%_100%)] hover:bg-[hsl(25_95%_48%)] rounded-full",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-4",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
